// app/guides/by-city.tsx — קורסים לפי עיר בישראל | Kursi
import Head from 'expo-router/head';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { useRouter, Link } from 'expo-router';
import { theme } from '@/constants/theme';

const SITE_URL = 'https://kursiaii.com';

const breadcrumbSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
    { '@type': 'ListItem', position: 3, name: 'קורסים לפי עיר', item: `${SITE_URL}/guides/by-city` },
  ],
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'האם יש הבדל בין קורסים בתל אביב לערים אחרות?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'תל אביב והמרכז מציעים את המגוון הרחב ביותר של קורסים פרונטליים, בעיקר בתחומי הטכנולוגיה. ירושלים, חיפה ובאר שבע מציעות מגוון קטן יותר אך עדיין משמעותי. קורסים אונליין זמינים מכל מקום.',
      },
    },
    {
      '@type': 'Question',
      name: 'האם כדאי לנסוע לקורס פרונטלי בעיר אחרת?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'תלוי בתחום ובמטרה. לבוטקאמפ טכנולוגי — יש הצדקה לנסוע למרכז אם אין אפשרות מקומית. לקורס כללי יותר — שווה לבחון אם יש אפשרות אונליין איכותית שתחסוך זמן נסיעה.',
      },
    },
    {
      '@type': 'Question',
      name: 'האם קורסים פרונטליים בפריפריה איכותיים פחות?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'לא בהכרח. יש קורסים מצוינים בחיפה, ירושלים, באר שבע ובמקומות נוספים. הבדיקה צריכה להיות על איכות המרצה, הסילבוס ופרויקטים של בוגרים — לא על מיקום.',
      },
    },
  ],
});

const CITIES = [
  {
    name: 'תל אביב והמרכז',
    description: 'המגוון הרחב ביותר. עשרות מכללות ומרכזי הכשרה. מרוכז בעיקר בת"א, פתח תקווה, רמת גן, ראשון לציון.',
    highlights: ['מעל 200 קורסים פרונטליים', 'בוטקאמפים טכנולוגיים מובילים', 'רשת קשרים טכנולוגית גדולה', 'גישה לשוק העבודה הגדול'],
    online: 'כל הקורסים זמינים גם אונליין',
  },
  {
    name: 'חיפה והצפון',
    description: 'מרכז טכנולוגי משמעותי עם טכניון בסמוך. קורסים בתחומי תכנות, הנדסה וטכנולוגיה.',
    highlights: ['קרבה לאקוסיסטם הטכנולוגי של חיפה', 'קורסי טכנולוגיה ממוקדים', 'מחירים לרוב נמוכים ממרכז הארץ', 'פחות תחרות על מקומות'],
    online: 'רוב הקורסים זמינים אונליין',
  },
  {
    name: 'ירושלים',
    description: 'מגוון קורסים בתחומי הטכנולוגיה, העיצוב והניהול. קרבה לאוניברסיטה העברית ולמוסדות אקדמיים.',
    highlights: ['קורסי אקדמיה ומיומנויות', 'קרבה למוסדות השכלה גבוהה', 'קהילת טכנולוגיה צומחת', 'מגוון קורסים לשוק הממשלתי'],
    online: 'רוב הקורסים זמינים אונליין',
  },
  {
    name: 'באר שבע והדרום',
    description: 'סייברפארק ומרכז טכנולוגי מתפתח. קורסי סייבר וטכנולוגיה בצמיחה.',
    highlights: ['מתמחה בסייבר ואבטחת מידע', 'קרבה לסייברפארק', 'אפשרויות קורסים דרך מרכזי ההכשרה האזוריים', 'מחירים תחרותיים'],
    online: 'רוב הקורסים זמינים אונליין',
  },
];

export default function ByCityPage() {
  const router = useRouter();

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>קורסים לפי עיר בישראל 2026 — תל אביב, חיפה, ירושלים, באר שבע | Kursi</title>
          <meta
            name="description"
            content="קורסים מקצועיים לפי עיר בישראל: תל אביב, חיפה, ירושלים, באר שבע. מדריך לבחירת קורס פרונטלי לפי מיקום גיאוגרפי, כולל השוואה לקורסים אונליין."
          />
          <link rel="canonical" href={`${SITE_URL}/guides/by-city`} />
          <meta property="og:url" content={`${SITE_URL}/guides/by-city`} />
          <meta property="og:title" content="קורסים לפי עיר בישראל 2026 | Kursi" />
          <meta
            property="og:description"
            content="קורסים מקצועיים לפי עיר: תל אביב, חיפה, ירושלים, באר שבע — מה זמין, מה מיוחד בכל מיקום, ומתי כדאי ללכת על אונליין."
          />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={`${SITE_URL}/og-image.webp`} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
        </Head>
      )}

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <View style={styles.navBar}>
          <Link
            href="/courses"
            style={styles.navBtn}
            accessibilityRole="link"
          >
            <Text style={styles.navBtnText}>← קורסים</Text>
          </Link>
          <Link
            href="/"
            style={[styles.navBtn, styles.navBtnHome]}
            accessibilityRole="link"
          >
            <Text style={[styles.navBtnText, styles.navBtnHomeText]}>דף הבית</Text>
          </Link>
        </View>

        <View style={styles.innerContent}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>לפי עיר</Text>
          </View>

          <Text
            style={styles.h1}
            {...(Platform.OS === 'web' && ({ role: 'heading', 'aria-level': 1 } as any))}
          >
            קורסים מקצועיים לפי עיר בישראל
          </Text>

          <Text style={styles.intro}>
            בוחרים קורס פרונטלי? המיקום חשוב. מדריך זה יעזור לכם להבין מה זמין בכל עיר,
            מה ייחודי בכל מרכז, ומתי כדאי לבחור באונליין במקום.
          </Text>

          {/* Online note */}
          <View style={styles.noteCard}>
            <Text style={styles.noteTitle}>שימו לב: קורסים אונליין זמינים מכל מקום</Text>
            <Text style={styles.noteBody}>
              אם אתם מתגוררים בפריפריה או מחפשים גמישות מלאה — קורסים אונליין נותנים גישה
              לכל הקורסים ללא הגבלת מיקום, לרוב במחיר נמוך יותר.
            </Text>
            <Link
              href="/compare/online-vs-frontal"
              style={styles.noteBtn}
              accessibilityRole="link"
            >
              <Text style={styles.noteBtnText}>השוואה: אונליין vs פרונטלי →</Text>
            </Link>
          </View>

          {/* City cards */}
          {CITIES.map((city, i) => (
            <View key={i} style={styles.cityCard}>
              <Text style={styles.cityName}>{city.name}</Text>
              <Text style={styles.cityDesc}>{city.description}</Text>
              <View style={styles.bulletList}>
                {city.highlights.map((h, j) => (
                  <View key={j} style={styles.bulletRow}>
                    <Text style={styles.bulletDot}>•</Text>
                    <Text style={styles.bulletText}>{h}</Text>
                  </View>
                ))}
              </View>
              <View style={styles.onlineTag}>
                <Text style={styles.onlineText}>{city.online}</Text>
              </View>
            </View>
          ))}

          {/* FAQ */}
          <View style={styles.section}>
            <Text style={styles.h2}>שאלות נפוצות</Text>
            {[
              { q: 'האם יש הבדל בין קורסים בתל אביב לערים אחרות?', a: 'תל אביב מציעה מגוון רחב יותר. ערים אחרות — מגוון קטן יותר אך איכות לא בהכרח נמוכה.' },
              { q: 'האם כדאי לנסוע לקורס בעיר אחרת?', a: 'לבוטקאמפ — כן. לקורס ממוקד — שווה לבחון אפשרות אונליין.' },
              { q: 'האם קורסים בפריפריה פחות טובים?', a: 'לא. הבדיקה צריכה להיות על איכות המרצה ופרויקטים של בוגרים, לא על מיקום.' },
            ].map((item, i) => (
              <View key={i} style={styles.faqItem}>
                <Text style={styles.faqQ}>{item.q}</Text>
                <Text style={styles.faqA}>{item.a}</Text>
              </View>
            ))}
          </View>

          {/* CTA */}
          <View style={styles.ctaBlock}>
            <Text style={styles.ctaTitle}>מחפשים קורס באזור שלכם?</Text>
            <Text style={styles.ctaBody}>ציינו עיר ותחום — ה-AI של Kursi יחזיר אפשרויות מתאימות, פרונטלי ואונליין.</Text>
            <Pressable
              style={({ pressed }) => [styles.ctaBtn, pressed && { opacity: 0.85 }]}
              onPress={() => router.push('/chat')}
              accessibilityRole="button"
            >
              <Text style={styles.ctaBtnText}>חפשו קורס לפי עיר</Text>
            </Pressable>
          </View>

          {/* Internal links */}
          <View style={styles.linksSection}>
            <Text style={styles.linksTitle}>מדריכים נוספים</Text>
            <View style={styles.linksGrid}>
              {[
                { label: 'קורס אונליין vs פרונטלי', route: '/compare/online-vs-frontal' },
                { label: 'בחירת קורס לפי תקציב', route: '/guides/by-budget' },
                { label: 'בחירת קורס לפי מטרה', route: '/guides/by-goal' },
                { label: 'קטלוג כל הקורסים', route: '/catalog' },
              ].map((link) => (
                <Pressable
                  key={link.route}
                  style={({ pressed }) => [styles.linkItem, pressed && { opacity: 0.75 }]}
                  onPress={() => router.push(link.route as any)}
                  accessibilityRole="link"
                >
                  <Text style={styles.linkText}>{link.label} →</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2EBDD' },
  content: { paddingBottom: 56 },
  innerContent: { maxWidth: 820, alignSelf: 'center' as any, width: '100%', paddingHorizontal: 16 },
  navBar: {
    flexDirection: 'row' as any, justifyContent: 'space-between' as any,
    alignItems: 'center' as any, paddingHorizontal: 16, paddingVertical: 12, gap: 10,
  },
  navBtn: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 0, backgroundColor: '#FAF5EA' },
  navBtnHome: { backgroundColor: '#FAF5EA' },
  navBtnPressed: { opacity: 0.7 },
  navBtnText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  navBtnHomeText: { color: '#7A6F5F' },
  badge: {
    backgroundColor: 'transparent', borderWidth: 1,
    borderColor: '#1A1611', borderRadius: 0,
    paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10,
    alignSelf: 'flex-end' as any, marginTop: 16,
  },
  badgeText: { fontSize: 12, fontFamily: 'Heebo-Bold', color: theme.colors.primary },
  h1: {
    fontSize: Platform.OS === 'web' ? 32 : 24, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 12, lineHeight: Platform.OS === 'web' ? 44 : 34,
  },
  intro: {
    fontSize: 16, fontFamily: 'Heebo-Regular', color: '#7A6F5F',
    textAlign: 'right' as any, lineHeight: 26, marginBottom: 20,
  },
  noteCard: {
    backgroundColor: '#EFF6FF', borderRadius: 14, borderWidth: 1,
    borderColor: '#BFDBFE', padding: 18, marginBottom: 20,
  },
  noteTitle: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1E40AF', textAlign: 'right' as any, marginBottom: 6 },
  noteBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#1E3A8A', textAlign: 'right' as any, lineHeight: 22, marginBottom: 12 },
  noteBtn: { alignSelf: 'flex-end' as any },
  noteBtnText: { fontSize: 14, fontFamily: 'Heebo-Bold', color: '#2563EB', textDecorationLine: 'underline' as any },
  cityCard: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  cityName: { fontSize: 20, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 8 },
  cityDesc: {
    fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A',
    textAlign: 'right' as any, lineHeight: 22, marginBottom: 12,
  },
  onlineTag: {
    marginTop: 12, backgroundColor: '#F0FDF4', borderRadius: 0,
    paddingVertical: 8, paddingHorizontal: 12, borderWidth: 1, borderColor: '#BBF7D0',
  },
  onlineText: { fontSize: 13, fontFamily: 'Heebo-Regular', color: '#166534', textAlign: 'right' as any },
  bulletList: { gap: 8 },
  bulletRow: { flexDirection: 'row-reverse' as any, gap: 8, alignItems: 'flex-start' as any },
  bulletDot: { fontSize: 14, color: theme.colors.primary, fontFamily: 'Heebo-Bold', lineHeight: 22 },
  bulletText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  section: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  h2: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 14, lineHeight: 28 },
  faqItem: { marginBottom: 14 },
  faqQ: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 6 },
  faqA: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  ctaBlock: { backgroundColor: '#FAF5EA', borderRadius: 0, padding: 24, alignItems: 'center' as any, marginTop: 8, marginBottom: 20 },
  ctaTitle: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center' as any, marginBottom: 8 },
  ctaBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' as any, marginBottom: 16, lineHeight: 22 },
  ctaBtn: { backgroundColor: theme.colors.primary, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 0 },
  ctaBtnText: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#FAF5EA' },
  linksSection: { marginBottom: 16 },
  linksTitle: { fontSize: 17, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 12 },
  linksGrid: { gap: 8 },
  linkItem: {
    paddingVertical: 12, paddingHorizontal: 16, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611',
  },
  linkText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary, textAlign: 'right' as any },
});
